/**
 * Campaign Landing Page - Redesigned to match ABMHC original styling
 */
import React, { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { PledgeProgressTracker, DonutChart, ProgressBar } from '@/components/PledgeProgressTracker';
import CampaignProgressTracker from '@/components/CampaignProgressTracker';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { submitDonation } from '@/lib/donationSubmit';
import AssemblyLeaderboard from '@/components/campaigns/AssemblyLeaderboard';
import {
  Heart, Target, Users, Calendar, MapPin, Mail, Phone,
  ArrowRight, ExternalLink, Image, FileText, MessageSquare, 
  TrendingUp, CheckCircle, Clock, AlertCircle, Camera, 
  BarChart3, Milestone, Bell, DollarSign
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
// ============== INLINE DONATE SECTION ==============
const CampaignDonateSection = ({ slug, campaign }) => {
  const tenantId = campaign?.tenant_id;
  const [amount, setAmount] = useState(100);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [donorVisibility, setDonorVisibility] = useState('anonymous');
  const [submitted, setSubmitted] = useState(false);

  // Fetch recent donations for the tenant
  const { data: recentDonations = [] } = useQuery({
    queryKey: ['campaign-donations-recent', tenantId],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/donations/public/${tenantId}/donations/recent`);
      return res.ok ? res.json() : [];
    },
    enabled: !!tenantId
  });

  const donateMutation = useMutation({
    mutationFn: (data) => submitDonation({ tenantId, payload: data }),
    onSuccess: (d) => {
      if (d.mode === 'gateway') return; // redirecting to the payment gateway
      setSubmitted(true);
      toast.success('Gift recorded — we will confirm it once your payment arrives');
    },
    onError: (e) => toast.error(e.message || 'Donation failed'),
  });

  if (!tenantId) return null;

  const presets = [25, 50, 100, 250, 500, 1000];

  if (submitted) {
    return (
      <section id="donate" className="py-16 bg-white">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-8 w-8 text-emerald-600" />
          </div>
          <h2 className="text-3xl font-bold text-slate-900 mb-2">Thank You!</h2>
          <p className="text-slate-600 mb-6" data-testid="campaign-donation-pending-note">
            Your gift has been recorded and is marked pending — we confirm it and send your
            receipt as soon as the payment is received.
          </p>
          <Button onClick={() => setSubmitted(false)} variant="outline">Make Another Donation</Button>
        </div>
      </section>
    );
  }

  return (
    <section id="donate" className="py-16 bg-white" data-testid="campaign-donate-section">
      <div className="max-w-6xl mx-auto px-4">
        <div className="grid lg:grid-cols-5 gap-10">
          {/* Donate Form */}
          <div className="lg:col-span-3">
            <p className="text-emerald-600 font-medium mb-2 uppercase tracking-wide text-sm">Support the Mission</p>
            <h2 className="text-3xl font-bold mb-6 text-slate-900">Make a Donation</h2>

            {/* Amount Presets */}
            <div className="mb-5">
              <Label className="text-sm font-medium mb-2 block">Select Amount ({campaign?.currency || 'USD'})</Label>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-3">
                {presets.map(p => (
                  <button key={p} onClick={() => setAmount(p)}
                    className={`py-3 rounded-lg border text-sm font-semibold transition-colors ${
                      amount === p ? 'campaign-primary-bg text-white border-transparent' : 'bg-white border-slate-200 hover:border-emerald-300'
                    }`}>${p}</button>
                ))}
              </div>
              <div className="relative">
                <DollarSign className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <Input type="number" className="pl-8" value={amount} onChange={e => setAmount(Number(e.target.value))} />
              </div>
            </div>

            {/* Visibility */}
            <div className="mb-5">
              <Label className="text-sm font-medium mb-2 block">How would you like to appear?</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[
                  { v: 'public', l: 'Public', d: 'Full name shown' },
                  { v: 'partial', l: 'Partial', d: 'First name + initial' },
                  { v: 'anonymous', l: 'Anonymous', d: 'Show as Anonymous' },
                  { v: 'private', l: 'Private', d: 'Hidden completely' },
                ].map(opt => (
                  <button key={opt.v} onClick={() => setDonorVisibility(opt.v)}
                    className={`p-2 rounded-lg border text-left transition-all ${
                      donorVisibility === opt.v ? 'bg-emerald-50 border-emerald-400 ring-1 ring-emerald-400' : 'bg-white border-slate-200'
                    }`} data-testid={`donate-vis-${opt.v}`}>
                    <p className={`text-xs font-medium ${donorVisibility === opt.v ? 'text-emerald-700' : 'text-slate-700'}`}>{opt.l}</p>
                    <p className="text-[10px] text-slate-400">{opt.d}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Donor Info */}
            <div className="space-y-3 mb-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label>Name {donorVisibility === 'anonymous' || donorVisibility === 'private' ? <span className="text-xs text-slate-400">(admin only)</span> : null}</Label><Input value={name} onChange={e => setName(e.target.value)} placeholder="Your name" data-testid="donate-name" /></div>
                <div><Label>Email <span className="text-xs text-slate-400">(for receipt)</span></Label><Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" data-testid="donate-email" /></div>
              </div>
              <div><Label>Phone <span className="text-xs text-slate-400">(optional)</span></Label><Input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+263 77 123 4567" /></div>
              <div><Label>Message <span className="text-xs text-slate-400">(optional, shown publicly)</span></Label><Textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="Share a word of encouragement..." rows={2} /></div>
            </div>

            <Button size="lg" className="w-full campaign-primary-bg text-white h-12 text-base" data-testid="donate-submit"
              onClick={() => donateMutation.mutate({ donor_name: name, donor_email: email, donor_phone: phone, amount, donation_type: 'one_time', message, is_anonymous: donorVisibility === 'anonymous' || donorVisibility === 'private', donor_visibility: donorVisibility })}
              disabled={!amount || donateMutation.isPending}>
              {donateMutation.isPending ? 'Processing...' : `Donate $${amount.toLocaleString()}`}
            </Button>
          </div>

          {/* Sidebar: Recent Donors */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-emerald-50 rounded-xl p-5 border border-emerald-100">
              <h3 className="font-semibold text-sm text-emerald-900 mb-3">Your Impact</h3>
              <div className="space-y-2 text-sm text-emerald-800">
                <p><strong>$25</strong> — Medical supplies for a day</p>
                <p><strong>$100</strong> — Funds one hospital bed</p>
                <p><strong>$500</strong> — Equips a consultation room</p>
                <p><strong>$1,000</strong> — Sponsors a ward section</p>
              </div>
            </div>
            {recentDonations.length > 0 && (
              <div className="bg-white rounded-xl p-5 border">
                <h3 className="font-semibold text-sm text-slate-900 mb-3">Recent Supporters</h3>
                <div className="space-y-3">
                  {recentDonations.slice(0, 5).map((d, i) => (
                    <div key={i} className="text-sm">
                      <div className="flex items-center justify-between">
                        <span className="text-slate-600 font-medium">{d.donor_name || 'Anonymous'}</span>
                        <span className="font-semibold text-emerald-700">${d.amount}</span>
                      </div>
                      {d.message && <p className="text-xs text-slate-400 mt-0.5 italic">"{d.message}"</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// ============== PLEDGE FORM SECTION ==============
const PledgeFormSection = ({ slug, campaign }) => {
  const [form, setForm] = useState({
    donor_name: '', donor_email: '', donor_phone: '',
    amount: '', frequency: 'one_time', payment_method: 'cash',
    message: '', preferred_contact: 'email', whatsapp_number: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [showPledges, setShowPledges] = useState(false);

  // Fetch payment methods for this campaign
  const { data: paymentMethodsData } = useQuery({
    queryKey: ['campaign-methods', slug],
    queryFn: async () => {
      const r = await fetch(`${API_URL}/api/campaigns/${slug}/pledges/public`);
      return r.ok ? r.json() : { pledges: [], summary: {} };
    }
  });

  // Get enabled payment methods from campaign
  const methods = (campaign?.payment_methods || []).filter(m => m.enabled);
  const defaultMethods = [
    { id: 'cash', name: 'Cash' },
    { id: 'bank_transfer', name: 'Bank Transfer' },
  ];
  const activeMethods = methods.length > 0 ? methods : defaultMethods;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.donor_name || !form.amount) return;
    if (!form.donor_email && !form.donor_phone) {
      toast.error('Please provide an email or phone number');
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (v) fd.append(k, v); });
      const res = await fetch(`${API_URL}/api/campaigns/${slug}/pledge`, { method: 'POST', body: fd });
      const data = await res.json();
      if (res.ok) {
        setResult(data);
        toast.success(data.message);
      } else {
        toast.error(data.detail || 'Failed to submit pledge');
      }
    } catch {
      toast.error('Network error');
    } finally {
      setSubmitting(false);
    }
  };

  const publicPledges = paymentMethodsData?.pledges || [];
  const summary = paymentMethodsData?.summary || {};

  if (result) {
    return (
      <section id="pledge-form" className="py-16 bg-white">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-8 w-8 text-emerald-600" />
          </div>
          <h2 className="text-3xl font-bold text-slate-900 mb-4">Thank You!</h2>
          <p className="text-slate-600 mb-4 text-lg">{result.message}</p>
          <Card className="text-left">
            <CardContent className="p-6 space-y-3">
              <div className="flex justify-between"><span className="text-slate-500">Reference</span><span className="font-mono font-bold">{result.reference}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Payment Method</span><span className="capitalize">{result.payment_method?.replace('_', ' ')}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Frequency</span><span className="capitalize">{result.frequency?.replace('_', ' ')}</span></div>

              {result.payment_method_details && (() => {
                const md = result.payment_method_details;
                const mid = result.payment_method;
                const returnUrl = typeof window !== 'undefined' ? window.location.origin + window.location.pathname : '';
                const linkUrl = md.payment_url
                  ? `${md.payment_url}${md.payment_url.includes('?') ? '&' : '?'}return_url=${encodeURIComponent(returnUrl)}&ref=${encodeURIComponent(result.reference)}`
                  : null;
                return (
                  <div className="pt-3 border-t space-y-2" data-testid="pledge-method-details">
                    <p className="text-sm font-medium text-slate-700">How to Pay — {md.name || mid}</p>
                    {(mid === 'ecocash' || mid === 'innbucks') && md.merchant_code && (
                      <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 space-y-1">
                        <p className="text-xs text-slate-600">Merchant Code</p>
                        <code className="block font-mono font-bold text-lg text-emerald-700" data-testid="pledge-confirm-merchant-code">{md.merchant_code}</code>
                        {md.merchant_name && <p className="text-xs text-slate-600">Merchant: {md.merchant_name}</p>}
                        <p className="text-xs text-slate-600">Use reference <code className="font-mono">{result.reference}</code></p>
                      </div>
                    )}
                    {mid === 'link' && linkUrl && (
                      <a href={linkUrl} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg"
                        data-testid="pledge-confirm-payment-link"
                      >Pay ${Number(form.amount || result.amount || 0).toLocaleString()} Now <ExternalLink className="h-4 w-4" /></a>
                    )}
                    {mid === 'bank_transfer' && (md.bank_name || md.account_number) && (
                      <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-sm text-slate-700 space-y-1">
                        {md.bank_name && <p><strong>Bank:</strong> {md.bank_name}</p>}
                        {md.account_name && <p><strong>Account Name:</strong> {md.account_name}</p>}
                        {md.account_number && <p><strong>Account #:</strong> <code className="font-mono">{md.account_number}</code></p>}
                        {md.branch_code && <p><strong>Branch:</strong> {md.branch_code}</p>}
                        {md.swift_code && <p><strong>SWIFT:</strong> {md.swift_code}</p>}
                      </div>
                    )}
                    {mid === 'paypal' && md.paypal_email && (
                      <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-sm"><strong>PayPal:</strong> {md.paypal_email}</div>
                    )}
                    {md.instructions && (
                      <p className="text-xs text-slate-600">{md.instructions}</p>
                    )}
                  </div>
                );
              })()}

              {!result.payment_method_details && result.payment_instructions && (
                <div className="pt-3 border-t">
                  <p className="text-sm font-medium text-slate-700 mb-1">Payment Instructions:</p>
                  <p className="text-sm text-slate-600">{result.payment_instructions}</p>
                </div>
              )}
            </CardContent>
          </Card>
          <Button className="mt-6" onClick={() => { setResult(null); setForm({ donor_name: '', donor_email: '', donor_phone: '', amount: '', frequency: 'one_time', payment_method: 'cash', message: '', preferred_contact: 'email', whatsapp_number: '' }); }}>
            Make Another Pledge
          </Button>
        </div>
      </section>
    );
  }

  return (
    <section id="pledge-form" className="py-16 bg-white">
      <div className="max-w-6xl mx-auto px-4">
        <div className="grid md:grid-cols-2 gap-12">
          {/* Pledge Form */}
          <div>
            <p className="text-emerald-600 font-medium mb-2 uppercase tracking-wide text-sm">Make a Commitment</p>
            <h2 className="text-3xl font-bold mb-6 text-slate-900">Pledge Your Support</h2>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="pledge-form">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Full Name *</label>
                <Input data-testid="pledge-name" value={form.donor_name} onChange={e => setForm(f => ({...f, donor_name: e.target.value}))} placeholder="Your full name" required />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                  <Input data-testid="pledge-email" type="email" value={form.donor_email} onChange={e => setForm(f => ({...f, donor_email: e.target.value}))} placeholder="email@example.com" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Phone (with country code)</label>
                  <Input data-testid="pledge-phone" value={form.donor_phone} onChange={e => setForm(f => ({...f, donor_phone: e.target.value}))} placeholder="+263 77 123 4567" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Pledge Amount ($) *</label>
                  <Input data-testid="pledge-amount" type="number" min="1" step="0.01" value={form.amount} onChange={e => setForm(f => ({...f, amount: e.target.value}))} placeholder="100.00" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Frequency</label>
                  <select data-testid="pledge-frequency" value={form.frequency} onChange={e => setForm(f => ({...f, frequency: e.target.value}))} className="w-full h-10 border border-slate-200 rounded-md px-3 text-sm">
                    <option value="one_time">One Time</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Payment Method</label>
                <select data-testid="pledge-method" value={form.payment_method} onChange={e => setForm(f => ({...f, payment_method: e.target.value}))} className="w-full h-10 border border-slate-200 rounded-md px-3 text-sm">
                  {activeMethods.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
                {(() => {
                  const selected = activeMethods.find(m => m.id === form.payment_method);
                  if (!selected) return null;
                  const returnUrl = typeof window !== 'undefined' ? window.location.href : '';
                  const linkUrl = selected.payment_url
                    ? `${selected.payment_url}${selected.payment_url.includes('?') ? '&' : '?'}return_url=${encodeURIComponent(returnUrl)}`
                    : null;
                  return (
                    <div className="mt-2 text-sm">
                      {(selected.id === 'ecocash' || selected.id === 'innbucks') && selected.merchant_code && (
                        <div className="bg-emerald-50 border border-emerald-200 rounded-md p-2.5 flex flex-wrap items-center gap-2" data-testid={`pledge-merchant-code-${selected.id}`}>
                          <span className="text-slate-600">Merchant Code:</span>
                          <code className="font-mono font-bold text-emerald-700 bg-white border border-emerald-200 px-2 py-0.5 rounded">{selected.merchant_code}</code>
                          {selected.merchant_name && <span className="text-xs text-slate-500">({selected.merchant_name})</span>}
                        </div>
                      )}
                      {selected.id === 'link' && linkUrl && (
                        <a href={linkUrl} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 mt-1 text-emerald-700 hover:text-emerald-800 underline font-medium"
                          data-testid="pledge-payment-link"
                        >Open Payment Link <ExternalLink className="h-3.5 w-3.5" /></a>
                      )}
                      {selected.id === 'bank_transfer' && (selected.bank_name || selected.account_number) && (
                        <div className="bg-slate-50 border border-slate-200 rounded-md p-2.5 text-xs text-slate-700 space-y-0.5">
                          {selected.bank_name && <div><span className="text-slate-500">Bank:</span> {selected.bank_name}</div>}
                          {selected.account_name && <div><span className="text-slate-500">Account:</span> {selected.account_name}</div>}
                          {selected.account_number && <div><span className="text-slate-500">Account #:</span> <code className="font-mono">{selected.account_number}</code></div>}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Preferred Contact</label>
                <div className="flex gap-4">
                  {['email', 'whatsapp', 'sms', 'other'].map(ch => (
                    <label key={ch} className="flex items-center gap-1.5 text-sm cursor-pointer">
                      <input type="radio" name="contact" value={ch} checked={form.preferred_contact === ch} onChange={e => setForm(f => ({...f, preferred_contact: e.target.value}))} className="accent-emerald-600" />
                      <span className="capitalize">{ch}</span>
                    </label>
                  ))}
                </div>
              </div>
              {form.preferred_contact === 'whatsapp' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">WhatsApp Number</label>
                  <Input value={form.whatsapp_number} onChange={e => setForm(f => ({...f, whatsapp_number: e.target.value}))} placeholder="+263 77 123 4567" />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Message (optional)</label>
                <textarea data-testid="pledge-message" value={form.message} onChange={e => setForm(f => ({...f, message: e.target.value}))} placeholder="Share a word of encouragement..." className="w-full border border-slate-200 rounded-md p-3 text-sm min-h-[80px]" />
              </div>
              <Button data-testid="pledge-submit" type="submit" disabled={submitting} className="w-full campaign-primary-bg text-white">
                {submitting ? 'Submitting...' : 'Submit Pledge'}
              </Button>
            </form>
          </div>

          {/* Pledges List */}
          <div>
            <p className="text-emerald-600 font-medium mb-2 uppercase tracking-wide text-sm">Community Support</p>
            <h2 className="text-3xl font-bold mb-6 text-slate-900">Pledges Made</h2>
            {summary.pledge_count > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
                <Card><CardContent className="p-4 text-center"><div className="text-2xl font-bold text-emerald-600">{summary.pledge_count}</div><div className="text-xs text-slate-500">Pledgers</div></CardContent></Card>
                <Card><CardContent className="p-4 text-center"><div className="text-2xl font-bold text-emerald-600">${summary.total_pledged?.toLocaleString()}</div><div className="text-xs text-slate-500">Pledged</div></CardContent></Card>
                <Card><CardContent className="p-4 text-center"><div className="text-2xl font-bold text-emerald-600">${summary.total_paid?.toLocaleString()}</div><div className="text-xs text-slate-500">Received</div></CardContent></Card>
              </div>
            )}
            <div className="space-y-3 max-h-[500px] overflow-y-auto">
              {publicPledges.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <Heart className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p>Be the first to pledge!</p>
                </div>
              ) : publicPledges.map(p => (
                <Card key={p.id} className="hover:shadow-sm transition-shadow">
                  <CardContent className="p-4 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-slate-900">{p.donor_name}</p>
                      <div className="flex gap-2 items-center text-xs text-slate-500">
                        <Badge variant="outline" className="text-xs capitalize">{p.frequency?.replace('_', ' ')}</Badge>
                        {p.message && <span className="italic truncate max-w-[200px]">"{p.message}"</span>}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-emerald-600">${p.amount?.toLocaleString()}</p>
                      <p className="text-xs text-slate-400">{p.status === 'fulfilled' ? 'Fulfilled' : p.paid_amount > 0 ? `$${p.paid_amount} paid` : 'Pledged'}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const CampaignLanding = () => {
  const { slug } = useParams();
  const [email, setEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  
  // Check for preview mode from URL params
  const urlParams = new URLSearchParams(window.location.search);
  const isPreview = urlParams.get('preview') === 'true';
  // Helper — preserve `?preview=true` on all internal campaign links so admins
  // viewing draft campaigns can navigate through the funnel without hitting 404s.
  const withPreview = (path) => (isPreview ? `${path}${path.includes('?') ? '&' : '?'}preview=true` : path);

  // Fetch campaign data
  const { data: campaign, isLoading } = useQuery({
    queryKey: ['campaign', slug, isPreview],
    queryFn: async () => {
      const url = isPreview 
        ? `${API_URL}/api/campaigns/${slug}?preview=true`
        : `${API_URL}/api/campaigns/${slug}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error('Campaign not found');
      return res.json();
    }
  });

  // Extract theme settings from campaign
  const theme = campaign?.page_config?.theme || {
    primaryColor: '#059669',
    accentColor: '#14b8a6',
  };

  // Apply theme as CSS variables
  const themeStyles = {
    '--campaign-primary': theme.primaryColor,
    '--campaign-accent': theme.accentColor,
  };

  // Fetch milestones
  const { data: milestones = [] } = useQuery({
    queryKey: ['campaign-milestones', slug],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/campaigns/${slug}/milestones`);
      return res.json();
    },
    enabled: !!campaign
  });

  // Fetch recent updates
  const { data: updates = [] } = useQuery({
    queryKey: ['campaign-updates', slug],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/campaigns/${slug}/updates`);
      return res.json();
    },
    enabled: !!campaign
  });

  // Fetch gallery
  const { data: gallery = [] } = useQuery({
    queryKey: ['campaign-gallery', slug],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/campaigns/${slug}/gallery`);
      return res.json();
    },
    enabled: !!campaign
  });

  // Subscribe to newsletter
  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setSubscribing(true);
    try {
      const formData = new FormData();
      formData.append('email', email);
      
      const res = await fetch(`${API_URL}/api/campaigns/${slug}/subscribe`, {
        method: 'POST',
        body: formData
      });
      
      if (res.ok) {
        toast.success('Subscribed successfully!');
        setEmail('');
      }
    } catch (error) {
      toast.error('Failed to subscribe');
    }
    setSubscribing(false);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white">
        <Skeleton className="h-[60vh] w-full" />
        <div className="max-w-6xl mx-auto px-4 py-12">
          <Skeleton className="h-8 w-1/2 mb-4" />
          <Skeleton className="h-24 w-full" />
        </div>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Card className="p-8 text-center">
          <h2 className="text-xl font-semibold mb-2">Campaign Not Found</h2>
          <p className="text-slate-500 mb-4">This campaign may have ended or doesn't exist.</p>
          <Link to="/campaigns">
            <Button>View All Campaigns</Button>
          </Link>
        </Card>
      </div>
    );
  }

  const progressPercent = campaign.target_amount > 0 
    ? Math.min(100, Math.round((campaign.raised_amount / campaign.target_amount) * 100))
    : 0;

  const completedMilestones = milestones.filter(m => m.status === 'completed').length;

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle className="h-4 w-4 text-emerald-600" />;
      case 'in_progress': return <Clock className="h-4 w-4 text-amber-500" />;
      default: return <AlertCircle className="h-4 w-4 text-slate-400" />;
    }
  };

  return (
    <div className="min-h-screen bg-white" style={themeStyles}>
      {/* Preview Mode Banner */}
      {(isPreview || campaign?._preview_mode) && (
        <div className="bg-amber-500 text-white py-2 px-4 text-center">
          <div className="flex items-center justify-center gap-2">
            <AlertCircle className="h-4 w-4" />
            <span className="font-medium">Preview Mode</span>
            <span className="text-amber-100">- This campaign is not published yet. Only you can see this page.</span>
          </div>
        </div>
      )}
      
      {/* Custom CSS injection for theme colors */}
      <style>{`
        .campaign-primary { color: var(--campaign-primary, #059669); }
        .campaign-primary-bg { background-color: var(--campaign-primary, #059669); }
        .campaign-primary-bg:hover { background-color: color-mix(in srgb, var(--campaign-primary, #059669) 85%, black); }
        .campaign-accent { color: var(--campaign-accent, #14b8a6); }
        .campaign-accent-bg { background-color: var(--campaign-accent, #14b8a6); }
        .campaign-gradient { background: linear-gradient(135deg, var(--campaign-primary, #059669), var(--campaign-accent, #14b8a6)); }
        .campaign-border { border-color: var(--campaign-primary, #059669); }
        ${campaign?.page_config?.customCss || ''}
      `}</style>
      
      {/* Top Header Bar */}
      <div className="bg-slate-900 text-white py-2 px-4">
        <div className="max-w-6xl mx-auto flex justify-between items-center text-sm">
          <div className="flex gap-4">
            {campaign.contact_email && (
              <a href={`mailto:${campaign.contact_email}`} className="flex items-center gap-1 hover:opacity-80 campaign-accent">
                <Mail className="h-3 w-3" />
                {campaign.contact_email}
              </a>
            )}
            {campaign.contact_phone && (
              <a href={`tel:${campaign.contact_phone}`} className="flex items-center gap-1 hover:opacity-80 campaign-accent">
                <Phone className="h-3 w-3" />
                {campaign.contact_phone}
              </a>
            )}
          </div>
          <Link to={withPreview(`/campaigns/${slug}/donate`)}>
            <Button size="sm" className="campaign-primary-bg text-white h-7 text-xs">
              Support Our Mission
            </Button>
          </Link>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[500px]">
        {campaign.hero_image ? (
          campaign.hero_image.includes('.mp4') || campaign.hero_image.includes('.webm') || campaign.hero_image.includes('.mov') ? (
            <video
              src={campaign.hero_image.startsWith('http') ? campaign.hero_image : `${process.env.REACT_APP_BACKEND_URL}${campaign.hero_image}`}
              autoPlay
              muted
              loop
              playsInline
              className="w-full h-full object-cover"
            />
          ) : (
            <img
              src={campaign.hero_image.startsWith('http') ? campaign.hero_image : `${process.env.REACT_APP_BACKEND_URL}${campaign.hero_image}`}
              alt={campaign.name}
              className="w-full h-full object-cover"
            />
          )
        ) : (
          <div className="w-full h-full campaign-gradient" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />
        
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-6xl mx-auto px-4 text-white">
            {campaign.logo_url && (
              <img
                src={campaign.logo_url.startsWith('http') ? campaign.logo_url : `${process.env.REACT_APP_BACKEND_URL}${campaign.logo_url}`}
                alt={`${campaign.name} logo`}
                className="h-16 md:h-20 mb-4 rounded-lg bg-white/95 p-2 shadow-md object-contain"
                data-testid="campaign-hero-logo"
              />
            )}
            {campaign.hero_kicker && (
              <p className="campaign-accent font-medium mb-2 tracking-wide uppercase text-sm">
                {campaign.hero_kicker}
              </p>
            )}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 leading-tight max-w-3xl">
              {campaign.name}
            </h1>
            {campaign.tagline && (
              <p className="text-xl md:text-2xl text-white/90 mb-8 max-w-2xl">
                {campaign.tagline}
              </p>
            )}
            <div className="flex gap-4 flex-wrap">
              <Link to={withPreview(`/campaigns/${slug}/donate`)}>
                <Button size="lg" className="campaign-primary-bg text-white">
                  <Heart className="h-5 w-5 mr-2" />
                  Support the Project
                </Button>
              </Link>
              <Link to={`/campaigns/${slug}/updates`}>
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                  View Progress
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-slate-900 text-white py-6">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-3xl md:text-4xl font-bold campaign-accent">
                {campaign.overall_progress || 0}%
              </div>
              <div className="text-sm text-slate-400">Overall Progress</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-bold text-emerald-400">
                {completedMilestones}/{milestones.length}
              </div>
              <div className="text-sm text-slate-400">Milestones Complete</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-bold text-emerald-400">
                {updates.length}
              </div>
              <div className="text-sm text-slate-400">Progress Updates</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-bold text-emerald-400">
                {gallery.length}
              </div>
              <div className="text-sm text-slate-400">Photos & Videos</div>
            </div>
          </div>
        </div>
      </section>

      {/* Fundraising Progress Tracker */}
      {(campaign.goal_amount > 0 || campaign.target_amount > 0) && (
        <section className="py-12 bg-gradient-to-br from-slate-50 to-emerald-50/30">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-8">
              <p className="text-emerald-600 font-medium mb-2 uppercase tracking-wide text-sm">Fundraising Progress</p>
              <h2 className="text-3xl font-bold text-slate-900">Help Us Reach Our Goal</h2>
            </div>
            <CampaignProgressTracker slug={slug} />
          </div>
        </section>
      )}

      {/* Mission Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-emerald-600 font-medium mb-2 uppercase tracking-wide text-sm">Our Mission</p>
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-slate-900">
                {campaign.mission_statement ? 'Our Purpose' : 'About This Project'}
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6 text-lg">
                {campaign.mission_statement || campaign.description}
              </p>
              {campaign.description && campaign.mission_statement && (
                <p className="text-slate-600 leading-relaxed mb-6">
                  {campaign.description}
                </p>
              )}
              <Link to={`/campaigns/${slug}/contact`}>
                <Button variant="outline" className="border-emerald-600 text-emerald-600 hover:bg-emerald-50">
                  Learn More About Us
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {gallery.slice(0, 4).map((img, i) => (
                <img 
                  key={img.id || i}
                  src={img.image_url}
                  alt={img.title || `Gallery ${i + 1}`}
                  className={`rounded-lg object-cover ${i === 0 ? 'col-span-2 h-48' : 'h-32'}`}
                />
              ))}
              {gallery.length < 4 && (
                <div className="bg-emerald-100 rounded-lg flex items-center justify-center h-32">
                  <Camera className="h-8 w-8 text-emerald-400" />
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Milestones Section */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <p className="text-emerald-600 font-medium mb-2 uppercase tracking-wide text-sm">Construction Progress</p>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900">Project Milestones</h2>
            <p className="text-slate-500 mt-2">Track our journey from groundbreaking to completion</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {milestones.slice(0, 4).map((milestone, index) => (
              <Card key={milestone.id} className={`relative overflow-hidden ${
                milestone.status === 'completed' ? 'border-emerald-200 bg-emerald-50/50' :
                milestone.status === 'in_progress' ? 'border-amber-200 bg-amber-50/50' : ''
              }`}>
                <div className={`absolute top-0 left-0 right-0 h-1 ${
                  milestone.status === 'completed' ? 'bg-emerald-500' :
                  milestone.status === 'in_progress' ? 'bg-amber-500' : 'bg-slate-200'
                }`} />
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-3">
                    {getStatusIcon(milestone.status)}
                    <Badge variant={
                      milestone.status === 'completed' ? 'default' :
                      milestone.status === 'in_progress' ? 'secondary' : 'outline'
                    } className="text-xs">
                      {milestone.status.replace('_', ' ')}
                    </Badge>
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">{milestone.title}</h3>
                  {milestone.description && (
                    <p className="text-sm text-slate-500 mb-4 line-clamp-2">{milestone.description}</p>
                  )}
                  <div>
                    <div className="flex justify-between text-xs text-slate-500 mb-1">
                      <span>Progress</span>
                      <span className="font-medium">{milestone.progress}%</span>
                    </div>
                    <Progress value={milestone.progress} className="h-2" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {milestones.length > 4 && (
            <div className="text-center mt-8">
              <Link to={`/campaigns/${slug}/milestones`}>
                <Button variant="outline">
                  View All Milestones
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Latest Update Section */}
      {updates.length > 0 && (
        <section className="py-16 bg-slate-900 text-white">
          <div className="max-w-6xl mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              {updates[0].image_url && (
                <img 
                  src={updates[0].image_url}
                  alt={updates[0].title}
                  className="rounded-xl shadow-2xl"
                />
              )}
              <div>
                <p className="text-emerald-400 font-medium mb-2 uppercase tracking-wide text-sm">Latest Update</p>
                <h2 className="text-3xl font-bold mb-4">{updates[0].title}</h2>
                <p className="text-slate-300 leading-relaxed mb-6 line-clamp-4">
                  {updates[0].content}
                </p>
                <Link to={`/campaigns/${slug}/updates`}>
                  <Button className="bg-emerald-600 hover:bg-emerald-700">
                    Read Full Update
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Quick Links Grid */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-6">
            <Link to={`/campaigns/${slug}/milestones`} className="group">
              <Card className="h-full hover:shadow-lg transition-shadow hover:border-emerald-200">
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-emerald-200 transition-colors">
                    <Milestone className="h-6 w-6 text-emerald-600" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">Project Milestones</h3>
                  <p className="text-sm text-slate-500">Track our progress through each construction phase</p>
                </CardContent>
              </Card>
            </Link>

            <Link to={`/campaigns/${slug}/gallery`} className="group">
              <Card className="h-full hover:shadow-lg transition-shadow hover:border-emerald-200">
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-emerald-200 transition-colors">
                    <Camera className="h-6 w-6 text-emerald-600" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">Photo Gallery</h3>
                  <p className="text-sm text-slate-500">See the latest construction photos and videos</p>
                </CardContent>
              </Card>
            </Link>

            <Link to={`/campaigns/${slug}/updates`} className="group">
              <Card className="h-full hover:shadow-lg transition-shadow hover:border-emerald-200">
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-emerald-200 transition-colors">
                    <BarChart3 className="h-6 w-6 text-emerald-600" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">Transparency Reports</h3>
                  <p className="text-sm text-slate-500">View our financial reports and receipts</p>
                </CardContent>
              </Card>
            </Link>

            <Link to={`/campaigns/${slug}/contact`} className="group">
              <Card className="h-full hover:shadow-lg transition-shadow hover:border-emerald-200">
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-emerald-200 transition-colors">
                    <MessageSquare className="h-6 w-6 text-emerald-600" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">Get Involved</h3>
                  <p className="text-sm text-slate-500">Contact us to learn how you can help</p>
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>
      </section>

      {/* Donation CTA */}
      <section className="py-16 bg-emerald-600 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Every Donation Builds Hope</h2>
          <p className="text-emerald-100 text-lg mb-8 max-w-2xl mx-auto">
            Your support brings us closer to opening the doors of this hospital. 
            Together, we can transform healthcare for thousands of families.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Link to={withPreview(`/campaigns/${slug}/donate`)}>
              <Button size="lg" className="bg-white text-emerald-700 hover:bg-emerald-50">
                <Heart className="h-5 w-5 mr-2" />
                Make a Donation
              </Button>
            </Link>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10" onClick={() => { document.getElementById('pledge-form')?.scrollIntoView({ behavior: 'smooth' }); }}>
              Make a Pledge
            </Button>
          </div>
        </div>
      </section>

      {/* Inline Donate Section (visible when campaign has tenant_id) */}
      <CampaignDonateSection slug={slug} campaign={campaign} />

      {/* Pledge Form Section */}
      <PledgeFormSection slug={slug} campaign={campaign} />

      {/* Fundraising Progress & Newsletter */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Fundraising Progress */}
            <div>
              <p className="text-emerald-600 font-medium mb-2 uppercase tracking-wide text-sm">Funding Progress</p>
              <h2 className="text-2xl font-bold mb-6 text-slate-900">Help Us Reach Our Goal</h2>
              
              <Card>
                <CardContent className="p-6">
                  {/* Progress Display based on mode */}
                  {(campaign.progress_display_mode === 'figures' || campaign.progress_display_mode === 'both' || !campaign.progress_display_mode) && (
                    <div className="flex justify-between mb-3">
                      <span className="text-3xl font-bold text-emerald-600">
                        ${(campaign.raised_amount || 0).toLocaleString()}
                      </span>
                      <span className="text-slate-500">
                        of ${(campaign.target_amount || 0).toLocaleString()}
                      </span>
                    </div>
                  )}
                  <Progress value={campaign.overall_progress || progressPercent} className="h-4 mb-4" />
                  {(campaign.progress_display_mode === 'percentage' || campaign.progress_display_mode === 'both' || !campaign.progress_display_mode) && (
                    <p className="text-sm text-slate-500">{campaign.overall_progress || progressPercent}% of goal reached</p>
                  )}
                  {campaign.progress_display_mode === 'figures' && (
                    <p className="text-sm text-slate-500">
                      {((campaign.raised_amount || 0) / (campaign.target_amount || 1) * 100).toFixed(0)}% funded
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* How the assemblies stand. Renders nothing at all unless this
                  appeal publishes standings, so a quiet campaign stays quiet. */}
              <div className="mt-4">
                <AssemblyLeaderboard campaignSlug={slug} variant="public" />
              </div>
            </div>

            {/* Newsletter Signup */}
            <div>
              <p className="text-emerald-600 font-medium mb-2 uppercase tracking-wide text-sm">Stay Connected</p>
              <h2 className="text-2xl font-bold mb-4 text-slate-900">Join Our Community</h2>
              <p className="text-slate-600 mb-6">
                Be the first to know about construction milestones, community events, 
                and the impact of your support.
              </p>
              
              <ul className="space-y-2 mb-6">
                <li className="flex items-center gap-2 text-sm text-slate-600">
                  <CheckCircle className="h-4 w-4 text-emerald-500" />
                  Monthly construction progress updates
                </li>
                <li className="flex items-center gap-2 text-sm text-slate-600">
                  <CheckCircle className="h-4 w-4 text-emerald-500" />
                  Donation impact stories
                </li>
                <li className="flex items-center gap-2 text-sm text-slate-600">
                  <CheckCircle className="h-4 w-4 text-emerald-500" />
                  Milestone celebrations
                </li>
              </ul>

              <form onSubmit={handleSubscribe} className="flex gap-2">
                <Input 
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1"
                />
                <Button type="submit" disabled={subscribing} className="bg-emerald-600 hover:bg-emerald-700">
                  {subscribing ? '...' : 'Subscribe'}
                </Button>
              </form>
              <p className="text-xs text-slate-500 mt-2">We respect your privacy. Unsubscribe anytime.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <h3 className="font-bold text-lg mb-4">{campaign.name}</h3>
              <p className="text-slate-400 text-sm">{campaign.tagline}</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><Link to={`/campaigns/${slug}`} className="hover:text-white">Home</Link></li>
                <li><Link to={`/campaigns/${slug}/milestones`} className="hover:text-white">Milestones</Link></li>
                <li><Link to={`/campaigns/${slug}/gallery`} className="hover:text-white">Gallery</Link></li>
                <li><Link to={`/campaigns/${slug}/updates`} className="hover:text-white">Updates</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Get Involved</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><Link to={withPreview(`/campaigns/${slug}/donate`)} className="hover:text-white">Donate</Link></li>
                <li><Link to={`/campaigns/${slug}/contact`} className="hover:text-white">Contact Us</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                {campaign.contact_email && <li>{campaign.contact_email}</li>}
                {campaign.contact_phone && <li>{campaign.contact_phone}</li>}
                {campaign.contact_address && <li>{campaign.contact_address}</li>}
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-8 pt-8 text-center text-sm text-slate-500">
            © {new Date().getFullYear()} {campaign.name}. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CampaignLanding;
